import React, { useState, useEffect, useCallback } from 'react';
import { Network, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import {
  isTauri,
  PROXY_TRANSPORT_KEY,
  proxyCredentialsKey,
  proxyPasswordSessionKey,
} from '../utils/tauri';

/**
 * The proxy config is split the same way the extension state is, and for the
 * same reason:
 *
 *   apiboo-proxy-transport            DEVICE-GLOBAL — enabled/type/host/port/
 *     noProxy. Which proxy you must go through is a property of the NETWORK
 *     this machine sits on; every account on the device needs the same one,
 *     and none of it is secret.
 *
 *   apiboo-<accountId>-proxy-credentials   PER-ACCOUNT — `username`. Who you
 *     authenticate to that proxy as is a credential, and it identifies a
 *     person, not a device.
 *
 *   apiboo-<accountId>-proxy-password      PER-ACCOUNT, sessionStorage. The
 *     password was already session-only (never written to disk), but the key
 *     was device-global: a sign-out and a different sign-in happen in the SAME
 *     window, so account B inherited A's plaintext proxy password from
 *     sessionStorage without either of them doing anything unusual. Namespacing
 *     the key closes that, and clearProxyPassword() drops it outright at
 *     sign-out.
 *
 * The pre-namespace `apiboo-proxy-config` (which carried the username in the
 * device-global blob) is deleted once by purgeLegacyStorageKeys().
 */
const DEFAULT_PROXY = {
  enabled: false,
  type: 'http',
  host: '',
  port: 8080,
  username: '',
  password: '',
  noProxy: 'localhost,127.0.0.1',
};

const TRANSPORT_FIELDS = ['enabled', 'type', 'host', 'port', 'noProxy'];

function loadProxyConfig() {
  const config = { ...DEFAULT_PROXY };
  try {
    const raw = localStorage.getItem(PROXY_TRANSPORT_KEY);
    if (raw) {
      const transport = JSON.parse(raw) || {};
      for (const field of TRANSPORT_FIELDS) {
        if (transport[field] !== undefined) config[field] = transport[field];
      }
    }
  } catch {
    // unreadable transport — fall back to defaults
  }
  try {
    const rawCreds = localStorage.getItem(proxyCredentialsKey());
    if (rawCreds) config.username = JSON.parse(rawCreds)?.username || '';
  } catch {
    // unreadable credentials — leave the username blank
  }
  try {
    config.password = sessionStorage.getItem(proxyPasswordSessionKey()) || '';
  } catch {
    // no sessionStorage — password stays blank
  }
  return config;
}

function saveProxyConfig(config) {
  try {
    const transport = {};
    for (const field of TRANSPORT_FIELDS) transport[field] = config[field];
    localStorage.setItem(PROXY_TRANSPORT_KEY, JSON.stringify(transport));
    // Username: on disk, but only under this account's key.
    localStorage.setItem(proxyCredentialsKey(), JSON.stringify({ username: config.username || '' }));
    // Password: sessionStorage only — cleared when the window closes.
    if (config.password) {
      sessionStorage.setItem(proxyPasswordSessionKey(), config.password);
    } else {
      sessionStorage.removeItem(proxyPasswordSessionKey());
    }
  } catch {
    // ignore storage errors
  }
}

/* ── Settings → Proxy page ────────────────────────────────────────────────────
   Was a modal opened from a titlebar button; it is now its own page in the
   Settings modal, so it renders straight into that modal's scrolling body. */
const ProxySettings = () => {
  const [config, setConfig] = useState(loadProxyConfig);
  const [testStatus, setTestStatus] = useState(null); // null | 'loading' | { ok, message }

  const update = useCallback((key, value) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Persist config after each committed state change — outside the updater to
  // avoid side effects in React's pure updater contract (StrictMode double-invoke).
  useEffect(() => {
    saveProxyConfig(config);
  }, [config]);

  const handleTestConnection = useCallback(async () => {
    setTestStatus('loading');
    try {
      const proxyForTest = config.enabled ? {
        enabled: true,
        proxy_type: config.type,
        host: config.host,
        port: Number(config.port),
        username: config.username || null,
        password: config.password || null,
        no_proxy: config.noProxy || null,
      } : null;

      const result = await (async () => {
        if (isTauri()) {
          const { invoke } = await import('@tauri-apps/api/core');
          return invoke('send_http_request', {
            request: {
              method: 'GET',
              url: 'https://httpbin.org/ip',
              headers: {},
              body: null,
              proxy: proxyForTest,
            },
          });
        } else {
          const resp = await fetch('https://httpbin.org/ip');
          const data = await resp.json();
          return { status: resp.status, data: JSON.stringify(data) };
        }
      })();

      let ip = '(unknown)';
      try {
        const parsed = typeof result.data === 'string' ? JSON.parse(result.data) : result.data;
        ip = parsed?.origin || parsed?.ip || ip;
      } catch {
        // ignore JSON parse errors
      }

      setTestStatus({ ok: true, message: `Connected — your IP: ${ip}` });
    } catch (err) {
      const msg = typeof err === 'string' ? err : (err?.message || String(err));
      setTestStatus({ ok: false, message: `Connection failed: ${msg}` });
    }
  }, [config]);

  const isSystem = config.type === 'system';

  return (
    <>
      <div className="settings-section">
        <div className="settings-section-title">Connection</div>
        <div className="settings-row">
          <div className="min-w-0">
            <div className="settings-row-label">Enable Proxy</div>
            <div className="settings-row-desc">Route all requests through a proxy server</div>
          </div>
          <div style={{ flexShrink: 0 }}>
            <button
              type="button"
              role="switch"
              aria-checked={config.enabled}
              onClick={() => update('enabled', !config.enabled)}
              className={`settings-toggle${config.enabled ? ' settings-toggle--on' : ''}`}
            >
              <span className="settings-toggle-thumb" />
            </button>
          </div>
        </div>

        <div className="settings-row">
          <div className="settings-row-label">Proxy Type</div>
          <select
            value={config.type}
            onChange={(e) => update('type', e.target.value)}
            disabled={!config.enabled}
            className="settings-select-native"
            style={{ opacity: config.enabled ? 1 : 0.5 }}
          >
            <option value="http">HTTP</option>
            <option value="https">HTTPS</option>
            <option value="socks5">SOCKS5</option>
            <option value="system">System Proxy</option>
          </select>
        </div>
      </div>

      {!isSystem && (
        <div className="settings-section">
          <div className="settings-section-title">Address</div>
          <div className="settings-row">
            <div className="settings-row-label">Host</div>
            <input
              type="text"
              className="settings-text-input"
              style={{ width: 200, opacity: config.enabled ? 1 : 0.5 }}
              value={config.host}
              onChange={(e) => update('host', e.target.value)}
              placeholder="proxy.example.com"
              disabled={!config.enabled}
            />
          </div>
          <div className="settings-row">
            <div className="settings-row-label">Port</div>
            <input
              type="number"
              className="settings-text-input"
              style={{ width: 80, opacity: config.enabled ? 1 : 0.5 }}
              value={config.port}
              min={1}
              max={65535}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10);
                if (!Number.isNaN(n)) update('port', n);
              }}
              disabled={!config.enabled}
            />
          </div>
        </div>
      )}

      {!isSystem && (
        <div className="settings-section">
          <div className="settings-section-title">Authentication</div>
          <div className="settings-row">
            <div className="min-w-0">
              <div className="settings-row-label">Username</div>
              <div className="settings-row-desc">Leave blank if no auth required</div>
            </div>
            <input
              type="text"
              className="settings-text-input"
              style={{ width: 160, opacity: config.enabled ? 1 : 0.5 }}
              value={config.username}
              onChange={(e) => update('username', e.target.value)}
              placeholder="username"
              disabled={!config.enabled}
            />
          </div>
          <div className="settings-row">
            <div className="min-w-0">
              <div className="settings-row-label">Password</div>
              <div className="settings-row-desc">Must be re-entered each session</div>
            </div>
            <input
              type="password"
              className="settings-text-input"
              style={{ width: 160, opacity: config.enabled ? 1 : 0.5 }}
              value={config.password}
              onChange={(e) => update('password', e.target.value)}
              placeholder="not saved between sessions"
              disabled={!config.enabled}
            />
          </div>
        </div>
      )}

      <div className="settings-section">
        <div className="settings-section-title">Bypass</div>
        <div className="settings-row-desc" style={{ marginBottom: 8 }}>
          Comma-separated hosts or patterns to bypass the proxy (e.g.{' '}
          <code style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11 }}>localhost,*.internal</code>)
        </div>
        <textarea
          className="settings-textarea"
          value={config.noProxy}
          onChange={(e) => update('noProxy', e.target.value)}
          disabled={!config.enabled}
          rows={3}
          style={{ opacity: config.enabled ? 1 : 0.5 }}
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingBottom: 8 }}>
        <button
          type="button"
          className="modern-button"
          onClick={handleTestConnection}
          disabled={testStatus === 'loading'}
          style={{ width: '100%', justifyContent: 'center' }}
        >
          {testStatus === 'loading'
            ? <Loader size={13} style={{ animation: 'spin 1s linear infinite' }} />
            : <Network size={13} />
          }
          {testStatus === 'loading' ? 'Testing…' : 'Test Connection'}
        </button>

        {testStatus && testStatus !== 'loading' && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 12, color: testStatus.ok ? '#22c55e' : '#ef4444' }}>
            {testStatus.ok ? <CheckCircle size={13} /> : <AlertCircle size={13} />}
            {testStatus.message}
          </div>
        )}
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
};

export default ProxySettings;
export { loadProxyConfig, saveProxyConfig, DEFAULT_PROXY, PROXY_TRANSPORT_KEY };
